import React, { useEffect, useState } from "react";

type Slide = {
  id: string;
  label: string;
  previewUrl?: string;
};

type Props = {
  slides: Slide[];
  onOpen?: (slide: Slide) => void;
};

const ImageGeneratorDesignSheetSlider: React.FC<Props> = ({
  slides,
  onOpen,
}) => {
  const [index, setIndex] = useState(0);
  const count = slides.length;

  useEffect(() => {
    setIndex(current => (count === 0 ? 0 : Math.min(current, count - 1)));
  }, [count, slides]);

  if (!count) {
    return null;
  }

  const go = (direction: -1 | 1) => {
    setIndex(current => (current + direction + count) % count);
  };

  const current = slides[index];

  return (
    <div
      className="mx-auto w-full max-w-full outline-none"
      tabIndex={0}
      onKeyDown={event => {
        if (count < 2) {
          return;
        }
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          go(-1);
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          go(1);
        }
      }}
    >
      <div className="mx-auto flex w-fit max-w-full items-center gap-1.5 sm:gap-2">
        {count > 1 ? (
          <button
            type="button"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E5D5CE] bg-white text-secondary shadow-md transition hover:bg-gray sm:h-11 sm:w-11"
            onClick={() => go(-1)}
            aria-label="Previous sheet"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 5 8 12l7 7"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}

        <button
          type="button"
          className="block max-w-[calc(100vw-7rem)] overflow-hidden rounded-2xl border border-light bg-[#F7F7F7] p-3 sm:max-w-[min(100%,calc(100vw-8rem))] sm:p-4"
          onClick={() => current.previewUrl && onOpen?.(current)}
          disabled={!current.previewUrl}
        >
          {current.previewUrl ? (
            <img
              key={current.id}
              src={current.previewUrl}
              alt={current.label}
              className="block h-auto max-h-[min(78vh,980px)] w-auto max-w-full rounded-md object-contain"
              draggable={false}
            />
          ) : (
            <div className="flex aspect-[3/2] w-[min(100%,760px)] items-center justify-center text-sm text-secondary">
              Loading {current.label.toLowerCase()}…
            </div>
          )}
        </button>

        {count > 1 ? (
          <button
            type="button"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E5D5CE] bg-white text-secondary shadow-md transition hover:bg-gray sm:h-11 sm:w-11"
            onClick={() => go(1)}
            aria-label="Next sheet"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="m9 5 7 7-7 7"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}
      </div>

      <div className="mt-3 flex items-center justify-center gap-3">
        <p className="text-sm font-medium text-secondary">{current.label}</p>
        {count > 1 ? (
          <p className="text-xs text-tertiary">
            {index + 1} / {count}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default ImageGeneratorDesignSheetSlider;
